import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth.service';
import { User } from '../models/User';

interface AuthContext {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ roleName?: string; roleId?: number }>;
  register: (userData: any, role_id: number) => Promise<void>;
  registerOnly: (userData: any, role_id: number) => Promise<any>;
  logout: () => void;
  refreshAuthState: () => void;
}

const AuthContext = createContext<AuthContext | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedUser = localStorage.getItem('user');

        if (storedUser && storedUser !== 'undefined') {
          const parsedUser = JSON.parse(storedUser);

          if (authService.validateAuthState() && authService.isAuthenticated()) {
            setUser(new User(parsedUser));
            setIsAuthenticated(true);
          } else {
            authService.clearAuthData();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch {
        authService.clearAuthData();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        initializeAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const login = async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      // Clear any existing auth state before login
      authService.clearAuthData();
      setUser(null);
      setIsAuthenticated(false);
      
      const response = await authService.login({ email, password, rememberMe });

      if (response.success && response.user) {
        // Create a proper User object from the response
        const userData = new User({
          id: response.user.id,
          email: response.user.email,
          role_id: response.user.role_id,
          role: response.user.role,
          firstName: response.user.firstName || '',
          lastName: response.user.lastName || '',
          phone: response.user.phone,
    
          trainee: (response.user as any).trainees && (response.user as any).trainees.length > 0 ? (response.user as any).trainees[0] : null,
      
          trainingCenter: (response.user as any).trainingCenters && (response.user as any).trainingCenters.length > 0 ? (response.user as any).trainingCenters[0] : null,
        });
        
      
        
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('rememberMe', rememberMe ? 'true' : 'false');

        // Return role info for navigation
        const roleName = response.user?.role?.name;
        const roleId = response.user?.role_id;
        return { roleName, roleId };
      } else {
        throw new Error('Invalid login response');
      }
    } catch (error) {
      logout(); // Clear everything on failure
      throw error;
    }
  };

  const register = async (userData: any, role_id: number) => {
    try {
      const response = await authService.register({ ...userData, role_id });
      // For registration, we might need to handle email verification flow
      // Don't automatically log in the user
    } catch (error) {
      throw error;
    }
  };

  const registerOnly = async (userData: any, role_id: number) => {
    try {
      const response = await authService.register({ ...userData, role_id });
      return response; // Return the response so the calling component can handle the redirect
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('rememberMe');
  };

  const refreshAuthState = () => {
    try {
      const storedUser = localStorage.getItem('user');

      if (storedUser && storedUser !== 'undefined') {
        const parsedUser = JSON.parse(storedUser);

        if (authService.validateAuthState() && authService.isAuthenticated()) {
          setUser(new User(parsedUser));
          setIsAuthenticated(true);
        } else {
          authService.clearAuthData();
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        authService.clearAuthData();
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch {
      authService.clearAuthData();
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      login,
      register,
      registerOnly,
      logout,
      refreshAuthState
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
